import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authService } from '../services/authService'
import { KeyRound, Mail, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'

export function ForceChangePasswordPage() {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [recoveryEmail, setRecoveryEmail] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { user, updateUser, logout, emailValidationRequired } = useAuth()
  const navigate = useNavigate()

  // Modo "solo email": el usuario no necesita cambiar contraseña pero no tiene email
  const emailOnlyMode = user && !user.mustChangePassword && !user.email && emailValidationRequired

  // Si no necesita cambiar contraseña NI falta email, redirigir al dashboard
  if (user && !user.mustChangePassword && !emailOnlyMode) {
    navigate('/dashboard', { replace: true })
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!emailOnlyMode) {
      // Modo cambio de contraseña + email
      if (newPassword.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres')
        return
      }

      if (newPassword !== confirmPassword) {
        setError('Las contraseñas no coinciden')
        return
      }
    }

    if (!recoveryEmail || !recoveryEmail.trim()) {
      setError('El email es obligatorio')
      return
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(recoveryEmail)) {
      setError('El formato del email no es válido')
      return
    }

    setIsLoading(true)

    try {
      if (emailOnlyMode) {
        // Solo actualizar email (sin cambiar contraseña)
        const updatedUser = await authService.updateRecoveryEmail(recoveryEmail)
        updateUser(updatedUser)
        toast.success('Email actualizado. Verifica tu email para continuar.')
      } else {
        // Cambiar contraseña + email
        const updatedUser = await authService.forceChangePassword(
          newPassword,
          recoveryEmail
        )
        updateUser(updatedUser)
        toast.success('Contraseña actualizada. Verifica tu email para continuar.')
      }
      navigate('/verify-email', { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar los cambios')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen flex items-center justify-center gradient-viscofan dark:bg-dark-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-500/20 dark:bg-secondary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400/20 dark:bg-primary-400/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-md w-full relative">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 backdrop-blur-xl border border-transparent dark:border-slate-700">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center mb-4">
              {emailOnlyMode ? <Mail className="h-8 w-8 text-white" /> : <KeyRound className="h-8 w-8 text-white" />}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {emailOnlyMode ? 'Introduce tu Email' : 'Cambio de Contraseña'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {emailOnlyMode
                ? 'Necesitas configurar tu email para poder continuar.'
                : 'Por seguridad, debes establecer una nueva contraseña para continuar.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl p-4 flex items-center gap-3 animate-slide-in">
                <AlertCircle className="h-5 w-5 text-error-500 flex-shrink-0" />
                <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
              </div>
            )}

            {!emailOnlyMode && (
              <>
                <div>
                  <label htmlFor="newPassword" className="label">
                    Nueva Contraseña *
                  </label>
                  <div className="relative">
                    <input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="input pr-10"
                      placeholder="Mínimo 6 caracteres"
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">
                    Confirmar Contraseña *
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input pr-10"
                      placeholder="Repite la contraseña"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className={!emailOnlyMode ? "pt-4 border-t border-gray-200 dark:border-dark-border" : ""}>
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <label htmlFor="recoveryEmail" className="label mb-0">
                  Tu Email Personal *
                </label>
              </div>
              <input
                id="recoveryEmail"
                type="email"
                required
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                className="input"
                placeholder="tu@email.com"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Se enviará un código de verificación a este email.
              </p>
            </div>

            <div className="pt-4 space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn btn-primary flex items-center justify-center gap-2 py-3 text-base"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    Guardar y Continuar
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="w-full btn btn-secondary py-2"
              >
                Cerrar Sesión
              </button>
            </div>
          </form>
        </div>

        <p className="text-center text-sm text-white/60 dark:text-gray-400 mt-8">
          © {new Date().getFullYear()} Viscofan España S.L.U. Todos los derechos reservados.
        </p>
      </div>
    </div>
  )
}
